import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import api from "@/lib/api";
import { sportShort } from "@/lib/sport";
import { useAuth, useFavorites } from "@/lib/auth";
import { SectionHeading, Reveal } from "@/components/Section";
import { StatusPill, MatchupRows } from "@/components/GameBits";
import DivisionTabs from "@/components/DivisionTabs";
import { divisionOf } from "@/lib/divisions";
import { saveSnapshot, loadSnapshot, setLiveBadge } from "@/lib/offline";

export default function Scoreboards() {
  const [cards, setCards] = useState([]);
  const [filter, setFilter] = useState("All");
  const [division, setDivision] = useState("all");
  const [loading, setLoading] = useState(true);
  const [offlineAt, setOfflineAt] = useState(null);
  const { user } = useAuth();
  const { favorites } = useFavorites(user);

  useEffect(() => {
    let stop = false;
    const load = () =>
      api.get("/home/scoreboards")
        .then(({ data }) => {
          if (stop) return;
          const teams = data.teams || [];
          setCards(teams);
          setOfflineAt(null);
          saveSnapshot("scoreboards", teams);
          setLiveBadge(teams.filter((c) => c.game?.status?.state === "in").length);
        })
        .catch(() => {
          if (stop) return;
          const snap = loadSnapshot("scoreboards");
          if (snap?.data) {
            setCards(snap.data);
            setOfflineAt(snap.at);
          }
        })
        .finally(() => setLoading(false));
    load();
    const id = setInterval(load, 30000); // up-to-the-minute scores
    return () => { stop = true; clearInterval(id); };
  }, []);

  const sports = ["All", ...new Set(cards.map((c) => c.sport_label))];
  const shown = cards
    .filter((c) => division === "all" || divisionOf(c.league) === division)
    .filter((c) => filter === "All" || c.sport_label === filter)
    .slice()
    .sort((a, b) => (favorites.includes(b.slug) ? 1 : 0) - (favorites.includes(a.slug) ? 1 : 0));

  // The scoreboards stay hidden until a metroplex game is actually in progress
  if (!loading && cards.length === 0) return null;

  return (
    <section id="scoreboards" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-20 sm:px-8 lg:px-12" data-testid="scoreboards-section">
      <SectionHeading
        index="01"
        eyebrow="UP TO THE MINUTE"
        title="DFW Scoreboards"
        right={
          <div className="flex flex-col items-start gap-2 sm:items-end">
            <DivisionTabs value={division} onChange={setDivision} testid="scoreboard-division" />
            <div className="flex flex-wrap gap-2" data-testid="scoreboard-filters">
            {sports.map((s) => (
              <button
                key={s}
                data-testid={`scoreboard-filter-${s.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setFilter(s)}
                className={`rounded-sm border px-3 py-1 font-display text-sm tracking-widest transition-colors ${
                  filter === s ? "border-volt bg-volt text-ink" : "border-bline text-slate-400 hover:border-volt/50 hover:text-volt"
                }`}
              >
                {s === "All" ? "ALL" : sportShort(s).toUpperCase()}
              </button>
            ))}
            </div>
          </div>
        }
      />
      {offlineAt && (
        <p data-testid="scoreboards-offline" className="mb-4 mt-[-8px] inline-flex items-center gap-2 rounded-md border border-gold/40 bg-gold/10 px-3 py-1.5 font-score text-[10px] tracking-widest text-gold">
          OFFLINE — SHOWING LAST SCORES FROM {new Date(offlineAt).toLocaleString("en-US", { timeZone: "America/Chicago", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} CT
        </p>
      )}
      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-44 animate-pulse rounded-xl border border-bline bg-surface" />)}
        </div>
      ) : shown.length === 0 ? (
        <p className="rounded-xl border border-bline bg-surface p-10 text-center" data-testid="scoreboards-empty">
          <span className="font-display text-2xl tracking-wide text-white">NO GAMES IN PROGRESS</span>
          <span className="mt-2 block font-score text-xs text-slate-500">DFW scoreboards light up the moment a metroplex game kicks off — check the calendar below for what's next.</span>
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((c, i) => (
            <Reveal key={c.slug} delay={(i % 6) * 0.05}>
              <Link
                to={`/team/${c.slug}`}
                data-testid={`team-card-${c.slug}`}
                className="group relative block rounded-xl border border-bline bg-surface p-5 transition-all duration-200 hover:-translate-y-1 hover:border-volt/50"
                style={{ borderTopWidth: 3, borderTopColor: c.color }}
              >
                {c.game?.status?.state === "in" && (
                  <span data-testid={`live-badge-${c.slug}`} className="absolute -top-2.5 right-4 inline-flex items-center gap-1.5 rounded-sm bg-liversed px-2.5 py-1 font-display text-xs tracking-widest text-white shadow-lg shadow-liversed/40">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> LIVE
                  </span>
                )}
                {favorites.includes(c.slug) && (
                  <span data-testid={`favorite-badge-${c.slug}`} className="absolute -top-2.5 left-4 inline-flex items-center gap-1 rounded-sm bg-volt px-2 py-1 font-display text-xs tracking-widest text-ink shadow-lg">
                    <Star className="h-3 w-3 fill-ink" /> MY TEAM
                  </span>
                )}
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <p className="font-display text-xl leading-none tracking-wide text-white group-hover:text-volt">{c.name}</p>
                    <p className="mt-1 font-score text-[10px] tracking-widest text-slate-500">
                      {c.league} · {sportShort(c.sport_label).toUpperCase()}
                      {c.record && <span className="ml-2 text-slate-400">REC {c.record}</span>}
                    </p>
                  </div>
                  {c.game && <StatusPill event={c.game} />}
                </div>
                <MatchupRows event={c.game} />
                {c.last_final && c.game?.status?.state !== "post" && c.last_final.id !== c.game?.id && (
                  <p className="mt-3 border-t border-bline pt-2 font-score text-[10px] text-slate-500">
                    LAST: {c.last_final.away.abbr} {c.last_final.away.score} @ {c.last_final.home.abbr} {c.last_final.home.score}
                  </p>
                )}
              </Link>
            </Reveal>
          ))}
        </div>
      )}
    </section>
  );
}
